import { AgencyId, AgencyMemberId, CaregiverId, OfficeBranchId, OfficeId } from "../messages/ids";
import {
  AssocAgencyMembersParams,
  AssocCaregiversParams,
  OfficeBranch,
  OfficeCreateParams,
  OfficeEditParams,
  OfficeExtended,
} from "../messages/office";
import { Api as Api } from "./Api";
import { Endpoint } from "./endpoint.service";

/*! @ngInject */
export class OfficesService {
  offices: OfficeExtended[] | undefined = undefined;

  constructor(
    private api: Api,
    private endpoint: Endpoint,
    private $rootScope: ng.IRootScopeService
  ) {}

  /**
   * Get a list of agency offices
   */
  async getOffices(params: {onlyActives: boolean, refetch: boolean} = {
    onlyActives: false,
    refetch: false
  }) {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/offices",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
      },
    });

    if (!params.refetch && this.offices !== undefined){
      return params.onlyActives ? this.offices.filter(row => row.active) : this.offices;
    }

    return this.api.get<{ offices: OfficeExtended[] }>(url).then((res) => {
      this.offices = res.data.offices;
      return params.onlyActives ? this.offices.filter(row => row.active) : this.offices;
    });
  }

  /**
   * Update agency members offices list
   */
  updateAgencyMembersOffices = (
    agencyId: AgencyId,
    agencyMemberId: AgencyMemberId,
    data: AssocAgencyMembersParams
  ) => {
    const url = this.endpoint({
      path: "agencies/:agencyId/offices/agency_member/:agencyMemberId/assoc_agency_members",
      params: {
        agencyId: agencyId,
        agencyMemberId: agencyMemberId,
      },
    });

    return this.api.put(url, data);
  };

  /**
   * Update caregivers offices list
   */
  updateCaregiversOffices = (
    agencyId: AgencyId,
    agencyMemberId: AgencyMemberId,
    data: AssocCaregiversParams
  ) => {
    const url = this.endpoint({
      path: "agencies/:agencyId/offices/agency_member/:agencyMemberId/assoc_caregivers",
      params: {
        agencyId: agencyId,
        agencyMemberId: agencyMemberId,
      },
    });

    return this.api.put(url, data);
  };

  /**
   * Insert office
   */
  insertOffice = (agencyId: AgencyId, agencyMemberId: AgencyMemberId, data: OfficeCreateParams) => {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/office",
      params: {
        agencyId: agencyId,
        agencyMemberId: agencyMemberId,
      },
    });

    return this.api.post<OfficeExtended>(url, data).then(({ data }) =>
      this.offices?.push(data)
    );
  };

  /**
   * Update office
   */
  updateOffice = (
    agencyId: AgencyId,
    agencyMemberId: AgencyMemberId,
    officeId: OfficeId,
    data: OfficeEditParams
  ) => {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/office/:officeId",
      params: {
        agencyId: agencyId,
        agencyMemberId: agencyMemberId,
        officeId: officeId,
      },
    });

    return this.api.put<OfficeExtended>(url, data).then(({ data }) => {
      this.offices = this.offices?.map((office) => (office.id === officeId ? data : office));
    });
  };

  /**
   * Get all active office branches of the agency
   */
  getOfficeBranches = () => {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/office_branches",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
      },
    });

    return this.api.get<{ list: OfficeBranch[] }>(url).then((res) => res.data.list);
  };

  /**
   * Replace a caregiver's office branches
   */
  updateCaregiverOfficeBranches = (
    caregiverId: CaregiverId,
    officeBranchIds: OfficeBranchId[]
  ) => {
    const url = this.endpoint({
      path: "agencies/:agencyId/agency_members/:agencyMemberId/caregivers/:caregiverId/office_branches",
      params: {
        agencyId: this.$rootScope.agencyId,
        agencyMemberId: this.$rootScope.agencyMemberId,
        caregiverId: caregiverId,
      },
    });

    return this.api.put(url, { officeBranchIds });
  };
}
